import React, { FC, useRef } from "react"
import { useTranslation } from "react-i18next"
import styled from "@emotion/styled"
import Block from "@/components/Block"
import Container from "@/components/Container"
import Box from "@/components/Box"
import Typography from "@/components/Typography"
import SimpleFadeTextAnimation from "@/components/SimpleFadeTextAnimation"
import RevealTextAnimation from "@/components/RevealTextAnimation"
import { m } from "framer-motion"
import useScrollProgress from "@/components/hooks/useScrollProgress"

const ImageStackGrid = styled(Box)`
  align-self: center;
  width: min(100%, 1050px);
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: 100%;
`

const ImageStackItem = styled(Box)`
  grid-column-start: 1;
  grid-column-end: 2;
  grid-row-start: 1;
  grid-row-end: 2;
`

const PhilosophySection: FC = () => {
  const { t } = useTranslation("security")
  const wrapperRef = useRef()
  const { scrollYProgress } = useScrollProgress({ wrapperRef, threshold: 1 })

  return (
    <Block
      bg="backgroundSecurityWhite"
      color="primaryDeepBlack"
      variant="hero"
      ref={wrapperRef}
    >
      <Container textAlign="center" mb={[11, 5, 10]} overflow="hidden">
        <Typography variant="h1" mb={["20px", 2, 4]}>
          <RevealTextAnimation
            text={t("section_one.title") as string}
          ></RevealTextAnimation>
        </Typography>
        <Box width={["100%", 500, 875]} mx={"auto"}>
          <Typography variant="subtitle" color="#565F76" mb={[1, 0, 2]}>
            {t("section_one.subtitle_new")}
          </Typography>
        </Box>
        <Box width={["100%", 533, 665]} mx={"auto"}>
          <ImageStackGrid>
            <ImageStackItem>
              <Box width={[327, 533, 665]} mx="auto">
                <img
                  alt="safe dial base"
                  src={`${process.env.ASSETS_BASE_URL}/security-page/philosophy-section/safe_dial_base.webp`}
                  loading="lazy"
                  style={{ width: "100%", height: "100%" }}
                />
              </Box>
            </ImageStackItem>
            <ImageStackItem>
              <Box width={[327, 533, 665]} mx="auto">
                <m.div
                  animate={{ rotate: 90 * scrollYProgress }}
                  transition={{
                    duration: 0,
                    ease: "linear",
                    bounce: 0,
                  }}
                >
                  <img
                    alt="safe dial numbers"
                    src={`${process.env.ASSETS_BASE_URL}/security-page/philosophy-section/safe_dial_numbers.webp`}
                    loading="lazy"
                    style={{ width: "100%", height: "100%" }}
                  />
                </m.div>
              </Box>
            </ImageStackItem>
            <ImageStackItem zIndex={1}>
              <Box width={[327, 533, 665]} mx="auto">
                <img
                  alt="safe dial top"
                  src={`${process.env.ASSETS_BASE_URL}/security-page/philosophy-section/safe_dial_top.webp`}
                  loading="lazy"
                  style={{ width: "100%", height: "100%" }}
                />
              </Box>
            </ImageStackItem>
          </ImageStackGrid>
        </Box>
      </Container>
      <Container>
        <Typography variant="h1" mb={[8, 8, 10]}>
          <RevealTextAnimation
            text={t("section_one.eyebrow") as string}
          ></RevealTextAnimation>
        </Typography>

        <SimpleFadeTextAnimation>
          <Box width={[327, 448, 804]} mb={[7, 4, 8]}>
            <Typography variant="h5" mb={[1, 0.5, 1]}>
              {t("section_one.contents_0_title")}
            </Typography>
            <Typography variant="bodyCopy" color="#565F76">
              {t("section_one.contents_0_paragraph")}
            </Typography>
          </Box>
        </SimpleFadeTextAnimation>
        <SimpleFadeTextAnimation>
          <Box width={[327, 448, 804]}>
            <Typography variant="h5" mb={[1, 0.5, 1]}>
              {t("section_one.contents_1_title")}
            </Typography>
            <Typography variant="bodyCopy" color="#565F76">
              {t("section_one.contents_1_paragraph")}
            </Typography>
          </Box>
        </SimpleFadeTextAnimation>
      </Container>
    </Block>
  )
}

export default PhilosophySection
